import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Card, 
  Row, 
  Col, 
  Avatar, 
  Typography, 
  Tag, 
  Rate, 
  Button, 
  Divider, 
  Timeline, 
  Image, 
  Space, 
  Badge,
  Spin,
  DatePicker,
  Modal,
  Form,
  Input,
  Skeleton,
  Tooltip
} from 'antd';
import { 
  UserOutlined, 
  ClockCircleOutlined, 
  StarOutlined,
  CrownOutlined,
  CalendarOutlined,
  PhoneOutlined,
  MailOutlined,
  GlobalOutlined,
  GithubOutlined,
  TwitterOutlined,
  LinkedinOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { apiService } from '../../services/api';
import { MentorDetailResponse, MentorAvailabilityResponse, BootcampResponse, MentorBookingResponse } from '../../types';
import { mentorDetailQueryKeys, menteeProfileQueryKeys } from '../../lib/queryClient';
import { isSubscriptionUiActive } from '../../utils/subscriptionUiActive';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import 'dayjs/locale/tr';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// Bootcamp durum çevirileri
const getBootcampStatusLabel = (status: string): { label: string; color: string } => {
  const statusMap: { [key: string]: { label: string; color: string } } = {
    'Draft': { label: 'Taslak', color: 'default' },
    'Published': { label: 'Yayınlandı', color: 'blue' },
    'Started': { label: 'Başladı', color: 'green' },
    'Completed': { label: 'Tamamlandı', color: 'purple' },
    'Cancelled': { label: 'İptal Edildi', color: 'red' }
  };
  return statusMap[status] || { label: status, color: 'default' };
};

/** Menteinin başka/aynı mentörle çakışan (beklemede veya onaylı) randevisi var mı; slot [start, +1h). */
function getMenteeTimeConflict(
  slotStart: Dayjs,
  slotEnd: Dayjs,
  bookings: MentorBookingResponse[],
  viewingMentorId: number
): { blocked: boolean; shortLabel: string; fullLabel: string } {
  for (const b of bookings) {
    if (b.status !== 'Pending' && b.status !== 'Confirmed') continue;
    const bs = dayjs(b.startTime);
    const be = dayjs(b.endTime);
    if (bs.isBefore(slotEnd) && be.isAfter(slotStart)) {
      if (b.mentorId === viewingMentorId) {
        return {
          blocked: true,
          shortLabel: 'Mevcut talep (siz)',
          fullLabel: `Bu saatte bu mentörle zaten beklemede veya onaylı bir randevunuz var. Çakışan seans: ${bs.format('D MMM YYYY HH:mm')} – ${be.format('HH:mm')}.`,
        };
      }
      const other = b.mentor ? `${b.mentor.firstName} ${b.mentor.lastName}`.trim() : 'Başka bir mentör';
      return {
        blocked: true,
        shortLabel: 'Başka mentörle plan',
        fullLabel: `Aynı saat diliminde ${other} ile randevunuz var (${bs.format('D MMMM YYYY HH:mm')} – ${be.format('HH:mm')}). Aynı anda yalnızca bir seans alabilirsiniz.`,
      };
    }
  }
  return { blocked: false, shortLabel: '', fullLabel: '' };
}

const MentorDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user, isAuthenticated } = useAuth();
  const isCompany = user?.role === 'Company';
  const mentorId = id ? parseInt(id) : null;

  dayjs.locale('tr');

  const getAppointmentDateTime = (d: dayjs.Dayjs, time: string) => {
    const h = parseInt(time.split(':')[0] ?? '0', 10);
    return d.hour(h).minute(0).second(0).millisecond(0);
  };

  /** Hafta başı (Pzt); o gün geçmişse bu haftada ilk müsait gün = bugün. */
  const getThisWeekOrToday = () => {
    const weekStart = dayjs().startOf('week');
    const today = dayjs().startOf('day');
    return weekStart.isBefore(today, 'day') ? today : weekStart;
  };

  const isSubscriptionActive = isSubscriptionUiActive(user);
  
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [bookingModalVisible, setBookingModalVisible] = useState(false);
  const [form] = Form.useForm();

  const { data: mentorDetail, isLoading: loading, isError, error } = useQuery({
    queryKey: mentorDetailQueryKeys.detail(mentorId!),
    queryFn: async () => {
      const response = await apiService.getMentorDetail(mentorId!);
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    enabled: !!mentorId,
    staleTime: 1000 * 60 * 5,
  });

  const { data: skillsData, isLoading: skillsLoading } = useQuery({
    queryKey: mentorDetailQueryKeys.skills(mentorId!),
    queryFn: async () => {
      const response = await apiService.getMentorSkills(mentorId!);
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    enabled: !!mentorId,
    staleTime: 1000 * 60 * 5,
  });

  const { data: educationsData, isLoading: educationsLoading } = useQuery({
    queryKey: mentorDetailQueryKeys.educations(mentorId!),
    queryFn: async () => {
      const response = await apiService.getMentorEducations(mentorId!);
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    enabled: !!mentorId,
    staleTime: 1000 * 60 * 5,
  });

  const { data: workExperiencesData, isLoading: workExperiencesLoading } = useQuery({
    queryKey: mentorDetailQueryKeys.workExperiences(mentorId!),
    queryFn: async () => {
      const response = await apiService.getMentorWorkExperiences(mentorId!);
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    enabled: !!mentorId,
    staleTime: 1000 * 60 * 5,
  });

  const { data: projectsData, isLoading: projectsLoading } = useQuery({
    queryKey: mentorDetailQueryKeys.projects(mentorId!),
    queryFn: async () => {
      const response = await apiService.getMentorProjects(mentorId!);
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    enabled: !!mentorId,
    staleTime: 1000 * 60 * 5,
  });

  const { data: certificatesData, isLoading: certificatesLoading } = useQuery({
    queryKey: mentorDetailQueryKeys.certificates(mentorId!),
    queryFn: async () => {
      const response = await apiService.getMentorCertificates(mentorId!);
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    enabled: !!mentorId,
    staleTime: 1000 * 60 * 5,
  });

  const { data: bootcampsData, isLoading: bootcampsLoading } = useQuery({
    queryKey: mentorDetailQueryKeys.bootcamps(mentorId!),
    queryFn: async () => {
      const response = await apiService.getMentorBootcamps(mentorId!);
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    enabled: !!mentorId,
    staleTime: 1000 * 60 * 5,
  });

  const { data: servicesData, isLoading: servicesLoading } = useQuery({
    queryKey: mentorDetailQueryKeys.services(mentorId!),
    queryFn: async () => {
      const response = await apiService.getMentorServices(mentorId!);
      if (!response.success) throw new Error(response.message);
      return response.data ?? [];
    },
    enabled: !!mentorId,
    staleTime: 1000 * 60 * 5,
  });

  const { data: availabilities = [], isLoading: availabilityLoading } = useQuery({
    queryKey: mentorDetailQueryKeys.availability(mentorId!),
    queryFn: async () => {
      const response = await apiService.getMentorAvailability(mentorId!);
      if (!response.success) throw new Error(response.message);
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: !!mentorId,
    staleTime: 1000 * 60,
  });

  const { data: myMentorBookings = [] } = useQuery({
    queryKey: [...menteeProfileQueryKeys.bookings, 'mentor-detail-calendar', mentorId ?? 0] as const,
    queryFn: async (): Promise<MentorBookingResponse[]> => {
      const pageSize = 100;
      const all: MentorBookingResponse[] = [];
      let page = 1;
      for (;;) {
        const res = await apiService.getMentorBookingsByMentee(page, pageSize, {
          scheduleConflictsOnly: true,
        });
        if (!res.success) throw new Error(res.message);
        const list = res.data?.data ?? [];
        const total = res.data?.totalCount ?? list.length;
        all.push(...list);
        if (all.length >= total || list.length < pageSize) break;
        page += 1;
        if (page > 30) break; // en fazla ~3000; sınır
      }
      return all;
    },
    enabled: isAuthenticated && user?.role === 'Mentee' && !!mentorId,
    staleTime: 1000 * 60,
  });

  const { data: publishedBootcamps = [], isLoading: publishedBootcampsLoading } = useQuery({
    queryKey: mentorDetailQueryKeys.publishedBootcamps(mentorId!),
    queryFn: async () => {
      const response = await apiService.getMentorPublishedBootcamps(mentorId!);
      if (!response.success) throw new Error(response.message);
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: !!mentorId,
    staleTime: 1000 * 60 * 5,
  });

  const mentor = useMemo((): MentorDetailResponse | null => {
    if (!mentorDetail) return null;
    return {
      ...mentorDetail,
      skills: skillsData ?? mentorDetail.skills ?? [],
      educations: educationsData ?? mentorDetail.educations ?? [],
      workExperiences: workExperiencesData ?? mentorDetail.workExperiences ?? [],
      projects: projectsData ?? mentorDetail.projects ?? [],
      certificates: certificatesData ?? mentorDetail.certificates ?? [],
      bootcamps: bootcampsData ?? mentorDetail.bootcamps ?? [],
      services: servicesData ?? mentorDetail.services ?? [],
      availabilities: availabilities as MentorAvailabilityResponse[],
    };
  }, [mentorDetail, skillsData, educationsData, workExperiencesData, projectsData, certificatesData, bootcampsData, servicesData, availabilities]);

  const bookingMutation = useMutation({
    mutationFn: async (values: { notes?: string }) => {
      if (!mentor || !selectedTime || !user) throw new Error('Eksik bilgi');
      const startDateTime = getAppointmentDateTime(selectedDate, selectedTime);
      if (startDateTime.isBefore(dayjs())) {
        throw new Error('Geçmiş bir zaman için randevu alınamaz.');
      }
      const endDateTime = startDateTime.add(1, 'hour');
      const response = await apiService.createMentorBooking({
        mentorId: mentor.id,
        menteeId: user.id,
        startTime: startDateTime.format('YYYY-MM-DDTHH:mm:ss'),
        endTime: endDateTime.format('YYYY-MM-DDTHH:mm:ss'),
        notes: values.notes,
        price: mentor.hourlyRate || 0
      });
      if (!response.success) throw new Error(response.message);
      return response;
    },
    onSuccess: () => {
      setBookingModalVisible(false);
      setSelectedTime(null);
      form.resetFields();
      if (mentorId) queryClient.invalidateQueries({ queryKey: mentorDetailQueryKeys.availability(mentorId) });
      queryClient.invalidateQueries({ queryKey: menteeProfileQueryKeys.bookings });
      toast.success('Randevu talebiniz başarıyla gönderildi!');
    },
    onError: (err) => {
      const axiosData = (err as { response?: { data?: { message?: string } } })?.response?.data;
      const msg =
        axiosData?.message ||
        (err instanceof Error ? err.message : 'Randevu talebi gönderilemedi.');
      toast.error(msg);
    }
  });

  const handleBookingSubmit = (values: { notes?: string }) => {
    if (!mentor || !selectedTime || !user) return;
    const at = getAppointmentDateTime(selectedDate, selectedTime);
    if (at.isBefore(dayjs())) {
      toast.error('Geçmiş bir zaman için randevu alınamaz.');
      return;
    }
    bookingMutation.mutate(values);
  };

  const handleBookingCancel = () => {
    setBookingModalVisible(false);
    setSelectedTime(null);
    form.resetFields();
  };

  const profileImageUrl = useMemo(() => {
    return mentor ? apiService.getMentorProfileImageUrl(mentor.id) : null;
  }, [mentor?.id]);

  if (isError) {
    toast.error(error instanceof Error ? error.message : 'Mentor bilgileri yüklenemedi.');
    navigate('/mentor-search');
    return null;
  }

  const renderHeader = () => {
    if (loading && !mentor) {
      return (
        <Card className="app-content-card" style={{ marginBottom: '24px' }}>
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={8} md={6}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                  <Skeleton.Avatar active size={150} shape="circle" style={{ width: '150px', height: '150px' }} />
                </div>
                <div style={{ width: '100%' }}>
                  <Skeleton active paragraph={{ rows: 2, width: ['90%', '70%'] }} title={false} />
                </div>
              </div>
            </Col>
            <Col xs={24} sm={16} md={18}>
              <Skeleton active paragraph={{ rows: 4, width: ['100%', '95%', '90%'] }} title={false} />
            </Col>
          </Row>
        </Card>
      );
    }

    if (!mentor) return null;

    return (
      <Card className="app-content-card" style={{ marginBottom: '24px' }}>
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={8} md={6}>
            <div style={{ textAlign: 'center' }}>
              <Avatar 
                size={150} 
                src={profileImageUrl || undefined}
                icon={<UserOutlined />}
                style={{ marginBottom: '16px' }}
                alt={`${mentor.firstName} ${mentor.lastName} profil resmi`}
                onError={() => true}
              />
              
              <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: '4px' }}>
                {typeof mentor.rating === 'number' && mentor.rating > 0 && typeof mentor.reviewCount === 'number' && mentor.reviewCount > 0 ? (
                  <>
                    <Rate disabled value={mentor.rating} style={{ fontSize: '16px' }} allowHalf />
                    <Text type="secondary" style={{ marginLeft: '4px' }}>
                      {Number(mentor.rating).toFixed(1)} ({mentor.reviewCount} değerlendirme)
                    </Text>
                  </>
                ) : (
                  <Text type="secondary">Henüz değerlendirme yok</Text>
                )}
              </div>

              <Badge 
                status={mentor.availabilities.some(a => a.isAvailable) ? 'success' : 'default'} 
                text={mentor.availabilities.some(a => a.isAvailable) ? 'Müsait' : 'Müsait Değil'} 
              />
            </div>
          </Col>

          <Col xs={24} sm={16} md={18}>
            <div style={{ marginBottom: '16px' }}>
              <Title level={1} style={{ margin: 0, marginBottom: '8px' }}>
                {mentor.firstName} {mentor.lastName}
              </Title>
              {mentor.title && (
                <Text type="secondary" style={{ fontSize: '18px', marginBottom: '16px', display: 'block' }}>
                  {mentor.title}
                </Text>
              )}
              
              {mentor.bio && (
                <Paragraph style={{ fontSize: '16px', marginBottom: '16px' }}>
                  {mentor.bio}
                </Paragraph>
              )}
            </div>

            <Row gutter={[16, 16]}>

              {mentor.experienceYears && (
                <Col xs={24} sm={12}>
                  <Space>
                    <ClockCircleOutlined style={{ color: 'var(--text-secondary)' }} />
                    <Text>{mentor.experienceYears} yıl deneyim</Text>
                  </Space>
                </Col>
              )}

              {mentor.hourlyRate && (
                <Col xs={24} sm={12}>
                  <Space>
                    <StarOutlined style={{ color: '#059669' }} />
                    <Text strong style={{ color: '#059669', fontSize: '16px' }}>
                      ₺{mentor.hourlyRate}/saat
                    </Text>
                  </Space>
                </Col>
              )}

              {mentor.category && (
                <Col xs={24} sm={12}>
                  <Space>
                    <CrownOutlined style={{ color: 'var(--text-secondary)' }} />
                    <Tag color="blue">{mentor.category}</Tag>
                  </Space>
                </Col>
              )}
            </Row>

            <Divider />

            <Row gutter={[16, 16]}>
              {mentor.email && (
                <Col xs={24} sm={12}>
                  <Space>
                    <MailOutlined style={{ color: 'var(--text-secondary)' }} />
                    <Text>{mentor.email}</Text>
                  </Space>
                </Col>
              )}

              {mentor.phone && (
                <Col xs={24} sm={12}>
                  <Space>
                    <PhoneOutlined style={{ color: 'var(--text-secondary)' }} />
                    <Text>{mentor.phone}</Text>
                  </Space>
                </Col>
              )}

              {mentor.website && (
                <Col xs={24} sm={12}>
                  <Space>
                    <GlobalOutlined style={{ color: 'var(--text-secondary)' }} />
                    <a href={mentor.website} target="_blank" rel="noopener noreferrer">
                      {mentor.website}
                    </a>
                  </Space>
                </Col>
              )}

              {mentor.linkedIn && (
                <Col xs={24} sm={12}>
                  <Space>
                    <LinkedinOutlined style={{ color: 'var(--text-secondary)' }} />
                    <a href={mentor.linkedIn} target="_blank" rel="noopener noreferrer">
                      LinkedIn Profili
                    </a>
                  </Space>
                </Col>
              )}

              {mentor.github && (
                <Col xs={24} sm={12}>
                  <Space>
                    <GithubOutlined style={{ color: 'var(--text-secondary)' }} />
                    <a href={mentor.github} target="_blank" rel="noopener noreferrer">
                      GitHub Profili
                    </a>
                  </Space>
                </Col>
              )}

              {mentor.twitter && (
                <Col xs={24} sm={12}>
                  <Space>
                    <TwitterOutlined style={{ color: 'var(--text-secondary)' }} />
                    <a href={mentor.twitter} target="_blank" rel="noopener noreferrer">
                      Twitter Profili
                    </a>
                  </Space>
                </Col>
              )}
            </Row>

            {/* Sadece Company değilse randevu butonunu göster */}
            {!isCompany && (
              <div style={{ marginTop: '24px' }}>
                <Space>
                  {isAuthenticated && user?.role === 'Mentee' && !isSubscriptionActive ? (
                    <Button 
                      type="primary" 
                      size="large"
                      icon={<CrownOutlined />}
                      onClick={() => {
                        const panelPath = '/mentee-profile?tab=subscription';
                        navigate(panelPath);
                      }}
                    >
                      Abonelik Satın Al
                    </Button>
                  ) : (
                    // Mentor kullanıcıları birbirinden randevu alamaz, butonu gösterme
                    user?.role !== 'Mentor' && (
                      <Button 
                        type="primary" 
                        size="large"
                        onClick={() => {
                          if (!isAuthenticated) {
                            toast.warning('Randevu almak için giriş yapmalısınız.');
                            return;
                          }
                          if (user?.role !== 'Mentee') {
                            toast.warning('Sadece mentee kullanıcıları randevu alabilir.');
                            return;
                          }
                          if (!isSubscriptionActive) {
                            toast.warning('Randevu alabilmek için abone olmalısınız.');
                            const panelPath = '/mentee-profile?tab=subscription';
                            navigate(panelPath);
                            return;
                          }
                          const element = document.getElementById('availability-calendar');
                          if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }
                        }}
                      >
                        Randevu Al
                      </Button>
                    )
                  )}
                </Space>
              </div>
            )}
          </Col>
        </Row>
      </Card>
    );
  };

  const renderSkills = () => {
    if (loading && !mentor) {
      return (
        <Card className="app-content-card" title="Yetkinlikler" style={{ marginBottom: '24px' }}>
          <Skeleton active paragraph={{ rows: 3 }} />
        </Card>
      );
    }

    if (!mentor) return null;

    return (
      <Card className="app-content-card" title="Yetkinlikler" style={{ marginBottom: '24px' }}>
        <Spin spinning={skillsLoading}>
          {skillsLoading && (!mentor.skills || mentor.skills.length === 0) ? (
            <Skeleton active paragraph={{ rows: 2, width: ['100%', '90%'] }} title={false} />
          ) : mentor.skills && mentor.skills.length > 0 ? (
            <Row gutter={[8, 8]}>
              {mentor.skills.map((skill, index) => (
                <Col key={index}>
                  <Tag color="blue" style={{ margin: '4px' }}>
                    {skill.skillName}
                    {skill.skillLevel && ` (${skill.skillLevel})`}
                    {skill.experienceYears && ` - ${skill.experienceYears} yıl`}
                  </Tag>
                </Col>
              ))}
            </Row>
          ) : !skillsLoading && (
            <Text type="secondary">Henüz yetkinlik bilgisi eklenmemiş.</Text>
          )}
        </Spin>
      </Card>
    );
  };

  const renderEducations = () => {
    if (loading && !mentor) {
      return (
        <Card className="app-content-card" title="Eğitim Bilgileri" style={{ marginBottom: '24px' }}>
          <Skeleton active paragraph={{ rows: 4 }} />
        </Card>
      );
    }

    if (!mentor) return null;

    return (
      <Card className="app-content-card" title="Eğitim Bilgileri" style={{ marginBottom: '24px' }}>
        <Spin spinning={educationsLoading}>
          {educationsLoading && (!mentor.educations || mentor.educations.length === 0) ? (
            <Skeleton active paragraph={{ rows: 3, width: ['100%', '95%', '90%'] }} title={false} />
          ) : mentor.educations && mentor.educations.length > 0 ? (
            <Timeline>
              {mentor.educations.map((education, index) => (
                <Timeline.Item key={index}>
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      {education.schoolName}
                    </Title>
                    <Text type="secondary">
                      {education.department}
                      {education.degree && ` - ${education.degree}`}
                    </Text>
                    <br />
                    <Text type="secondary">
                      {dayjs(education.startDate).format('YYYY')} - {education.endDate ? dayjs(education.endDate).format('YYYY') : 'Devam ediyor'}
                    </Text>
                    {education.description && (
                      <Paragraph style={{ marginTop: '8px', marginBottom: 0 }}>
                        {education.description}
                      </Paragraph>
                    )}
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          ) : !educationsLoading && (
            <Text type="secondary">Henüz eğitim bilgisi eklenmemiş.</Text>
          )}
        </Spin>
      </Card>
    );
  };

  const renderWorkExperiences = () => {
    if (loading && !mentor) {
      return (
        <Card className="app-content-card" title="İş Deneyimi" style={{ marginBottom: '24px' }}>
          <Skeleton active paragraph={{ rows: 4 }} />
        </Card>
      );
    }

    if (!mentor) return null;

    return (
      <Card className="app-content-card" title="İş Deneyimi" style={{ marginBottom: '24px' }}>
        <Spin spinning={workExperiencesLoading}>
          {workExperiencesLoading && (!mentor.workExperiences || mentor.workExperiences.length === 0) ? (
            <Skeleton active paragraph={{ rows: 3, width: ['100%', '95%', '90%'] }} title={false} />
          ) : mentor.workExperiences && mentor.workExperiences.length > 0 ? (
            <Timeline>
              {mentor.workExperiences.map((work, index) => (
                <Timeline.Item key={index}>
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      {work.position} - {work.companyName}
                    </Title>
                    <Text type="secondary">
                      {dayjs(work.startDate).format('MMM YYYY')} - {(work.isCurrentJob || !work.endDate) ? 'Günümüz' : dayjs(work.endDate).format('MMM YYYY')}
                    </Text>
                    {work.description && (
                      <Paragraph style={{ marginTop: '8px', marginBottom: 0 }}>
                        {work.description}
                      </Paragraph>
                    )}
                    {work.achievements && (
                      <div style={{ marginTop: '8px' }}>
                        <Text strong>Başarılar:</Text>
                        <Paragraph style={{ marginTop: '4px', marginBottom: 0 }}>
                          {work.achievements}
                        </Paragraph>
                      </div>
                    )}
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          ) : !workExperiencesLoading && (
            <Text type="secondary">Henüz iş deneyimi bilgisi eklenmemiş.</Text>
          )}
        </Spin>
      </Card>
    );
  };

  const renderProjects = () => {
    if (loading && !mentor) {
      return (
        <Card className="app-content-card" title="Projeler" style={{ marginBottom: '24px' }}>
          <Row gutter={[16, 16]}>
            {[1, 2, 3].map(i => (
              <Col xs={24} sm={12} md={8} key={i}>
                <Card size="small">
                  <Skeleton active paragraph={{ rows: 2, width: ['100%', '90%'] }} title={false} />
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      );
    }

    if (!mentor) return null;

    return (
      <Card className="app-content-card" title="Projeler" style={{ marginBottom: '24px' }}>
        <Spin spinning={projectsLoading}>
          {projectsLoading && (!mentor.projects || mentor.projects.length === 0) ? (
            <Row gutter={[16, 16]}>
              {[1, 2, 3].map(i => (
                <Col xs={24} sm={12} md={8} key={i}>
                  <Card size="small">
                    <Skeleton active paragraph={{ rows: 2, width: ['100%', '90%'] }} title={false} />
                  </Card>
                </Col>
              ))}
            </Row>
          ) : mentor.projects && mentor.projects.length > 0 ? (
            <Row gutter={[16, 16]}>
              {mentor.projects.map((project, index) => (
                <Col xs={24} sm={12} md={8} key={index}>
                  <Card size="small" hoverable>
                    <Title level={5} style={{ margin: 0, marginBottom: '8px' }}>
                      {project.projectName}
                    </Title>
                    {project.projectDescription && (
                      <Paragraph style={{ marginBottom: '8px' }}>
                        {project.projectDescription.length > 100 
                          ? `${project.projectDescription.substring(0, 100)}...` 
                          : project.projectDescription}
                      </Paragraph>
                    )}
                    {project.technologies && (
                      <div style={{ marginBottom: '8px' }}>
                        {project.technologies.split(',').map((tech, i) => (
                          <Tag key={i} color="green" style={{ margin: '2px' }}>
                            {tech.trim()}
                          </Tag>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {project.githubUrl && (
                        <Button size="small" icon={<GithubOutlined />} href={project.githubUrl} target="_blank">
                          GitHub
                        </Button>
                      )}
                      {project.liveUrl && (
                        <Button size="small" icon={<GlobalOutlined />} href={project.liveUrl} target="_blank">
                          Canlı Demo
                        </Button>
                      )}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : !projectsLoading && (
            <Text type="secondary">Henüz proje bilgisi eklenmemiş.</Text>
          )}
        </Spin>
      </Card>
    );
  };

  const renderCertificates = () => {
    if (loading && !mentor) {
      return (
        <Card className="app-content-card" title="Sertifikalar" style={{ marginBottom: '24px' }}>
          <Row gutter={[16, 16]}>
            {[1, 2, 3].map(i => (
              <Col xs={24} sm={12} md={8} key={i}>
                <Card size="small">
                  <div className="skeleton-image-wrapper" style={{ height: '120px', width: '100%', marginBottom: '8px' }}>
                    <div className="skeleton-image-fill" />
                  </div>
                  <div style={{ width: '100%' }}>
                    <Skeleton active paragraph={{ rows: 2, width: ['100%', '90%'] }} title={false} />
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      );
    }

    if (!mentor) return null;

    return (
      <Card className="app-content-card" title="Sertifikalar" style={{ marginBottom: '24px' }}>
        <Spin spinning={certificatesLoading}>
          {certificatesLoading && (!mentor.certificates || mentor.certificates.length === 0) ? (
            <Row gutter={[16, 16]}>
              {[1, 2, 3].map(i => (
                <Col xs={24} sm={12} md={8} key={i}>
                  <Card size="small">
                    <div className="skeleton-image-wrapper" style={{ height: '120px', marginBottom: '8px' }}>
                      <div className="skeleton-image-fill" />
                    </div>
                    <Skeleton active paragraph={{ rows: 2, width: ['100%', '90%'] }} title={false} />
                  </Card>
                </Col>
              ))}
            </Row>
          ) : mentor.certificates && mentor.certificates.length > 0 ? (
            <Row gutter={[16, 16]}>
              {mentor.certificates.map((cert, index) => (
                <Col xs={24} sm={12} md={8} key={index}>
                  <Card size="small" hoverable>
                    {cert.certificateImage && (
                      <Image
                        src={`data:image/jpeg;base64,${cert.certificateImage}`}
                        alt={cert.certificateName}
                        style={{ width: '100%', height: '120px', objectFit: 'cover', marginBottom: '8px' }}
                      />
                    )}
                    <Title level={5} style={{ margin: 0, marginBottom: '4px' }}>
                      {cert.certificateName}
                    </Title>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {cert.issuingOrganization}
                    </Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {dayjs(cert.issueDate).format('MMM YYYY')}
                      {cert.expiryDate && ` - ${dayjs(cert.expiryDate).format('MMM YYYY')}`}
                    </Text>
                    {cert.credentialUrl && (
                      <div style={{ marginTop: '8px' }}>
                        <Button size="small" href={cert.credentialUrl} target="_blank">
                          Doğrula
                        </Button>
                      </div>
                    )}
                  </Card>
                </Col>
              ))}
            </Row>
          ) : !certificatesLoading && (
            <Text type="secondary">Henüz sertifika bilgisi eklenmemiş.</Text>
          )}
        </Spin>
      </Card>
    );
  };

  const renderBootcamps = () => {
    if (!mentor || mentor.bootcamps.length === 0) return null;

    return (
      <Card className="app-content-card" title="Mentor Bootcampleri" style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]}>
          {mentor.bootcamps.map((bootcamp, index) => (
            <Col xs={24} sm={12} md={8} key={bootcamp.id ?? index}>
              <Card 
                size="small" 
                hoverable
                onClick={() => navigate(`/bootcamp/${bootcamp.id}`)}
                styles={{ body: { cursor: 'pointer' } }}
                cover={
                  bootcamp.image ? (
                    <Image
                      src={`data:image/jpeg;base64,${bootcamp.image}`}
                      alt={bootcamp.title}
                      preview={false}
                      style={{ height: '120px', objectFit: 'cover' }}
                    />
                  ) : null
                }
              >
                <Title level={5} style={{ margin: 0, marginBottom: '8px' }}>
                  {bootcamp.title}
                </Title>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {dayjs(bootcamp.startDate).format('DD MMM YYYY')} - {dayjs(bootcamp.endDate).format('DD MMM YYYY')}
                </Text>
                <br />
                <Text strong style={{ color: '#059669' }}>
                  ₺{bootcamp.price}
                </Text>
                <br />
                <Tag color={getBootcampStatusLabel(bootcamp.status).color}>
                  {getBootcampStatusLabel(bootcamp.status).label}
                </Tag>
                <div style={{ marginTop: '8px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {bootcamp.currentParticipants}/{bootcamp.maxParticipants} katılımcı
                  </Text>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>
    );
  };

  const renderPublishedBootcamps = () => {
    if (publishedBootcamps.length === 0) return null;

    return (
      <Card className="app-content-card" title="Yayınlanan Bootcamp'ler" style={{ marginBottom: '24px' }}>
        <Spin spinning={publishedBootcampsLoading}>
          <Row gutter={[16, 16]}>
            {publishedBootcamps.map((bootcamp, index) => (
              <Col xs={24} sm={12} md={8} key={bootcamp.id ?? index}>
                <Card 
                  size="small" 
                  hoverable
                  cover={
                    bootcamp.image ? (
                      <Image
                        src={`data:image/jpeg;base64,${bootcamp.image}`}
                        alt={bootcamp.title}
                        preview={false}
                        style={{ height: '120px', objectFit: 'cover' }}
                      />
                    ) : null
                  }
                  actions={[
                    <Button 
                      type="primary" 
                      size="small" 
                      onClick={() => navigate(`/bootcamp/${bootcamp.id}`)}
                      style={{ width: '100%' }}
                    >
                      Detayları Gör
                    </Button>
                  ]}
                >
                  <Title level={5} style={{ margin: 0, marginBottom: '8px' }}>
                    {bootcamp.title}
                  </Title>
                  <Text type="secondary" style={{ fontSize: '12px', marginBottom: '8px', display: 'block' }}>
                    {dayjs(bootcamp.startDate).format('DD MMM YYYY')} - {dayjs(bootcamp.endDate).format('DD MMM YYYY')}
                  </Text>
                  
                  <div style={{ marginBottom: '8px' }}>
                    <Space>
                      <Tag color="blue">{bootcamp.isOnline ? 'Online' : 'Yüz Yüze'}</Tag>
                      <Tag color={getBootcampStatusLabel(bootcamp.status).color}>
                        {getBootcampStatusLabel(bootcamp.status).label}
                      </Tag>
                    </Space>
                  </div>

                  <div style={{ marginBottom: '8px' }}>
                    <Text strong style={{ color: '#059669', fontSize: '16px' }}>
                      ₺{bootcamp.price.toLocaleString()}
                    </Text>
                  </div>

                  <div style={{ marginBottom: '8px' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {bootcamp.currentParticipants}/{bootcamp.maxParticipants} katılımcı
                    </Text>
                  </div>

                  {bootcamp.description && (
                    <Text 
                      type="secondary" 
                      style={{ 
                        fontSize: '12px',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {bootcamp.description}
                    </Text>
                  )}
                </Card>
              </Col>
            ))}
          </Row>
        </Spin>
      </Card>
    );
  };

  const renderServices = () => {
    if (loading && !mentor) {
      return (
        <Card className="app-content-card" title="Hizmetler" style={{ marginBottom: '24px' }}>
          <Row gutter={[16, 16]}>
            {[1, 2, 3].map(i => (
              <Col xs={24} sm={12} md={8} key={i}>
                <Card size="small">
                  <Skeleton active paragraph={{ rows: 2, width: ['100%', '90%'] }} title={false} />
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      );
    }

    if (!mentor) return null;

    return (
      <Card className="app-content-card" title="Hizmetler" style={{ marginBottom: '24px' }}>
        <Spin spinning={servicesLoading}>
          {servicesLoading && (!mentor.services || mentor.services.length === 0) ? (
            <Row gutter={[16, 16]}>
              {[1, 2, 3].map(i => (
                <Col xs={24} sm={12} md={8} key={i}>
                  <Card size="small">
                    <Skeleton active paragraph={{ rows: 2, width: ['100%', '90%'] }} title={false} />
                  </Card>
                </Col>
              ))}
            </Row>
          ) : mentor.services && mentor.services.length > 0 ? (
            <Row gutter={[16, 16]}>
              {mentor.services.map((service, index) => (
                <Col xs={24} sm={12} md={8} key={index}>
                  <Card size="small" hoverable>
                    <Title level={5} style={{ margin: 0, marginBottom: '8px' }}>
                      {service.serviceName}
                    </Title>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {service.serviceType}
                    </Text>
                    <br />
                    <Text strong style={{ color: '#059669' }}>
                      ₺{service.price}
                    </Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {service.durationInMinutes} dakika
                    </Text>
                    {service.description && (
                      <Paragraph style={{ marginTop: '8px', marginBottom: 0, fontSize: '12px' }}>
                        {service.description.length > 100 
                          ? `${service.description.substring(0, 100)}...` 
                          : service.description}
                      </Paragraph>
                    )}
                  </Card>
                </Col>
              ))}
            </Row>
          ) : !servicesLoading && (
            <Text type="secondary">Henüz hizmet bilgisi eklenmemiş.</Text>
          )}
        </Spin>
      </Card>
    );
  };

  const renderAvailability = () => {
    if (!mentor) return null;
    
    // Company rolü müsaitlik takvimini görmesin
    if (isCompany) {
      return null;
    }
    
    // Giriş yapmış mentor kullanıcıları müsaitlik takvimini hiç görmesin
    if (isAuthenticated && user?.role === 'Mentor') {
      return null;
    }
    
    // Giriş yapmamış kullanıcılar için uyarı - ama sayfayı görebilirler
    if (!isAuthenticated) {
      return (
        <div id="availability-calendar">
          <Card className="app-content-card" title="Müsaitlik Takvimi" style={{ marginBottom: '24px' }}>
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <CalendarOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
              <Title level={4} type="secondary">
                Müsaitlik takvimini görüntülemek ve randevu almak için mentee olarak giriş yapmalısınız
              </Title>
            </div>
          </Card>
        </div>
      );
    }
    
    // Mentee değilse uyarı
    if (user?.role !== 'Mentee') {
      return (
        <div id="availability-calendar">
          <Card className="app-content-card" title="Müsaitlik Takvimi" style={{ marginBottom: '24px' }}>
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <CalendarOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
              <Title level={4} type="secondary">
                Müsaitlik takvimini görüntülemek için mentee olarak giriş yapmalısınız
              </Title>
            </div>
          </Card>
        </div>
      );
    }
    
    // Abonelik kontrolü - Aboneliği olmayan mentee kullanıcıları için uyarı
    if (!isSubscriptionActive) {
      return (
        <div id="availability-calendar">
          <Card className="app-content-card" title="Müsaitlik Takvimi" style={{ marginBottom: '24px' }}>
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <CrownOutlined style={{ fontSize: '48px', color: '#faad14', marginBottom: '16px' }} />
              <Title level={4} type="warning">
                Müsaitlik takvimini görüntülemek için abone olmalısınız
              </Title>
              <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
                Randevu alabilmek ve müsaitlik takvimini görüntüleyebilmek için aktif aboneliğiniz olmalıdır.
              </Text>
              <Button 
                type="primary" 
                size="large"
                icon={<CrownOutlined />}
                onClick={() => {
                  const panelPath = '/mentee-profile?tab=subscription';
                  navigate(panelPath);
                }}
              >
                Abonelik Satın Al
              </Button>
            </div>
          </Card>
        </div>
      );
    }

    // Haftalık müsaitlik verilerini oluştur
    const generateWeeklyAvailability = (startDate: dayjs.Dayjs) => {
      const weekDays = [];
      for (let i = 0; i < 7; i++) {
        const date = startDate.add(i, 'day');
        const dayName = date.format('dddd');
        const dayAvailabilities = availabilities.filter(avail => {
          const dayMapping: { [key: string]: string } = {
            'monday': 'Pazartesi',
            'tuesday': 'Salı', 
            'wednesday': 'Çarşamba',
            'thursday': 'Perşembe',
            'friday': 'Cuma',
            'saturday': 'Cumartesi',
            'sunday': 'Pazar'
          };
          
          // Hem gün adı hem de tarih kontrolü yap
          const dayMatch = avail.dayOfWeek === dayMapping[dayName.toLowerCase()];
          const dateMatch = dayjs(avail.startTime).format('YYYY-MM-DD') === date.format('YYYY-MM-DD');
          
          console.log(`Gün kontrolü - API: ${avail.dayOfWeek}, Beklenen: ${dayMapping[dayName.toLowerCase()]}, Eşleşme: ${dayMatch}`);
          console.log(`Tarih kontrolü - API: ${dayjs(avail.startTime).format('YYYY-MM-DD')}, Beklenen: ${date.format('YYYY-MM-DD')}, Eşleşme: ${dateMatch}`);
          
          return dayMatch || dateMatch;
        });
        
        console.log(`Gün: ${dayName}, Müsaitlik verileri:`, dayAvailabilities);
        
        // Saatlik slotları hesapla ve müsait olanları say
        const hourlySlots = [];
        for (let hour = 8; hour < 24; hour++) {
          const timeString = `${hour.toString().padStart(2, '0')}:00`;
          let isAvailable = false; // Varsayılan olarak müsait değil
          let availabilityStatus = 'not_set'; // 'available', 'unavailable', 'not_set'
          
          if (dayAvailabilities.length > 0) {
            const matchingAvailability = dayAvailabilities.find(avail => {
              const startTime = dayjs(avail.startTime).format('HH:mm');
              const endTime = dayjs(avail.endTime).format('HH:mm');
              return timeString >= startTime && timeString < endTime;
            });
            
            if (matchingAvailability) {
              isAvailable = matchingAvailability.isAvailable;
              availabilityStatus = matchingAvailability.isAvailable ? 'available' : 'unavailable';
            }
          }
          if (isAvailable && myMentorBookings.length) {
            const slotStart = date.hour(hour).minute(0).second(0).millisecond(0);
            if (getMenteeTimeConflict(slotStart, slotStart.add(1, 'hour'), myMentorBookings, mentor.id).blocked) {
              isAvailable = false;
            }
          }
          
          console.log(`Saat: ${timeString}, Müsait: ${isAvailable}, Durum: ${availabilityStatus}`);
          hourlySlots.push({ time: timeString, isAvailable, status: availabilityStatus });
        }
        
        const availableSlotCount = hourlySlots.filter(slot => slot.isAvailable).length;
        console.log(`Gün: ${dayName}, Müsait slot sayısı: ${availableSlotCount}`);
        
        weekDays.push({
          date,
          dayName: date.format('dddd'),
          dayNameShort: date.format('ddd'),
          dayNumber: date.format('D'),
          availabilities: dayAvailabilities,
          availableSlotCount: availableSlotCount
        });
      }
      return weekDays;
    };

    const weekDays = generateWeeklyAvailability(selectedDate.startOf('week'));
    const thisWeekViewDate = getThisWeekOrToday();

    // Saatlik müsaitlik verilerini oluştur
    const generateTimeSlots = (dayAvailabilities: any[]) => {
      const timeSlots: Array<{
        time: string;
        isAvailable: boolean;
        isInPast: boolean;
        menteeBlocksSlot: boolean;
        menteeBlockLabel: string;
        menteeBlockFullLabel: string;
        isSelected: boolean;
        status: string;
      }> = [];
      const startHour = 8; // 08:00'den başla
      const endHour = 24; // 00:00'a kadar (24:00 = 00:00)
      
      for (let hour = startHour; hour < endHour; hour++) {
        const timeString = `${hour.toString().padStart(2, '0')}:00`;
        
        // Backend'den gelen müsaitlik verilerini kontrol et
        let isAvailable = false; // Varsayılan olarak müsait değil
        let availabilityStatus = 'not_set'; // 'available', 'unavailable', 'not_set'
        
        if (dayAvailabilities.length > 0) {
          // Bu gün için müsaitlik verisi varsa kontrol et
          const matchingAvailability = dayAvailabilities.find(avail => {
            const startTime = dayjs(avail.startTime).format('HH:mm');
            const endTime = dayjs(avail.endTime).format('HH:mm');
            
            console.log(`Saat kontrolü - ${timeString}: ${startTime} - ${endTime}, Müsait: ${avail.isAvailable}`);
            
            // Saat aralığını kontrol et (örn: 09:00-17:00 arasında 10:00 müsait)
            return timeString >= startTime && timeString < endTime;
          });
          
          if (matchingAvailability) {
            isAvailable = matchingAvailability.isAvailable;
            availabilityStatus = matchingAvailability.isAvailable ? 'available' : 'unavailable';
            console.log(`Eşleşen müsaitlik bulundu: ${timeString} - ${isAvailable}`);
          }
        }
        // Eğer müsaitlik verisi yoksa varsayılan olarak müsait değil kabul et
        
        const slotAt = selectedDate.startOf('day').hour(hour).minute(0).second(0).millisecond(0);
        const isInPast = slotAt.isBefore(dayjs());
        const menteeC = getMenteeTimeConflict(slotAt, slotAt.add(1, 'hour'), myMentorBookings, mentor.id);
        const menteeBlocksSlot = !isInPast && isAvailable && menteeC.blocked;
        timeSlots.push({
          time: timeString,
          isAvailable,
          isInPast,
          menteeBlocksSlot,
          menteeBlockLabel: menteeC.shortLabel,
          menteeBlockFullLabel: menteeC.fullLabel,
          isSelected: selectedTime === timeString,
          status: availabilityStatus
        });
      }
      return timeSlots;
    };

    const selectedDayAvailabilities = weekDays.find(day => 
      day.date.format('YYYY-MM-DD') === selectedDate.format('YYYY-MM-DD')
    )?.availabilities || [];

    const timeSlots = generateTimeSlots(selectedDayAvailabilities);

    const handleDateChange = (date: dayjs.Dayjs | null) => {
      if (date && !date.isBefore(dayjs(), 'day')) {
        setSelectedDate(date);
        setSelectedTime(null);
        // Müsaitlik verileri zaten yüklü, sadece state'i güncelle
      }
    };

    const handleTimeSelect = (time: string) => {
      if (!isAuthenticated) {
        toast.warning('Randevu almak için giriş yapmalısınız.');
        return;
      }
      
      if (user?.role !== 'Mentee') {
        toast.warning('Sadece mentee kullanıcıları randevu alabilir.');
        return;
      }

      const at = getAppointmentDateTime(selectedDate, time);
      if (at.isBefore(dayjs())) {
        toast.error('Geçmiş bir saat için randevu alınamaz.');
        return;
      }
      const endAt = at.add(1, 'hour');
      const c = getMenteeTimeConflict(at, endAt, myMentorBookings, mentor.id);
      if (c.blocked) {
        toast.warning(c.fullLabel || c.shortLabel);
        return;
      }
      
      setSelectedTime(time);
      setBookingModalVisible(true);
    };


    const navigateWeek = (direction: 'prev' | 'next') => {
      const newDate = direction === 'prev' 
        ? selectedDate.subtract(1, 'week')
        : selectedDate.add(1, 'week');
      
      // Bugünden önceki haftalara gitmeyi engelle
      if (newDate.isBefore(dayjs(), 'day')) {
        return;
      }
      
      setSelectedDate(newDate);
      setSelectedTime(null);
    };

    return (
      <div id="availability-calendar">
        <Card 
          className="app-content-card"
          title="Müsaitlik Takvimi" 
          style={{ marginBottom: '24px' }}
        >
          <div style={{ marginBottom: '24px' }}>
          {/* Tarih Seçimi */}
          <div style={{ marginBottom: '24px' }}>
            <Text strong style={{ color: '#1f2937', marginBottom: '8px', display: 'block' }}>
              * Tarih Seçin
            </Text>
            
            {/* Hızlı Seçim Butonları */}
            <div style={{ marginBottom: '12px', display: 'flex', gap: '8px' }}>
              <Button 
                onClick={() => setSelectedDate(dayjs())}
                type="default"
                size="small"
                style={{
                  background: selectedDate.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD') ? 'var(--success)' : 'var(--bg-tertiary)',
                  color: selectedDate.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD') ? 'white' : '#000',
                  border: '1px solid #d9d9d9',
                  cursor: 'pointer'
                }}
              >
                Bugün
              </Button>
              <Button 
                onClick={() => {
                  setSelectedDate(getThisWeekOrToday());
                  setSelectedTime(null);
                }}
                type="default"
                size="small"
                style={{
                  background: selectedDate.format('YYYY-MM-DD') === thisWeekViewDate.format('YYYY-MM-DD') ? 'var(--success)' : 'var(--bg-tertiary)',
                  color: selectedDate.format('YYYY-MM-DD') === thisWeekViewDate.format('YYYY-MM-DD') ? 'white' : '#000',
                  border: '1px solid #d9d9d9',
                  cursor: 'pointer'
                }}
              >
                Bu Hafta
              </Button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', maxWidth: '500px' }}>
              <Button 
                onClick={() => navigateWeek('prev')}
                type="default"
                disabled={selectedDate.isSame(dayjs(), 'day') || selectedDate.isBefore(dayjs(), 'day')}
                style={{ 
                  background: 'var(--bg-tertiary)',
                  border: '1px solid #d9d9d9', 
                  borderRadius: '6px',
                  height: '40px',
                  width: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: (selectedDate.isSame(dayjs(), 'day') || selectedDate.isBefore(dayjs(), 'day')) ? 'not-allowed' : 'pointer',
                  opacity: (selectedDate.isSame(dayjs(), 'day') || selectedDate.isBefore(dayjs(), 'day')) ? 0.5 : 1
                }}
              >
                ←
              </Button>
              
              <DatePicker
                value={selectedDate}
                onChange={(date) => handleDateChange(date)}
                format="DD/MM/YYYY"
                style={{ 
                  flex: 1, 
                  height: '40px',
                  borderRadius: '6px'
                }}
                placeholder="Tarih seçin"
                suffixIcon={<CalendarOutlined style={{ color: 'var(--text-secondary)' }} />}
                disabledDate={(current) => current && current.isBefore(dayjs(), 'day')}
              />
              
              <Button 
                onClick={() => navigateWeek('next')}
                type="default"
                style={{ 
                  background: 'var(--bg-tertiary)',
                  border: '1px solid #d9d9d9', 
                  borderRadius: '6px',
                  height: '40px',
                  width: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                →
              </Button>
            </div>
          </div>

          {/* Saat Seçimi */}
          <div style={{ marginBottom: '16px' }}>
            <Text strong style={{ color: '#1f2937', marginBottom: '8px', display: 'block' }}>
              * Saat Seçin
            </Text>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(8, 1fr)', 
              gap: '8px',
              maxWidth: '100%'
            }}>
              {timeSlots.map((slot) => {
                const canPick = slot.isAvailable && !slot.isInPast && !slot.menteeBlocksSlot;
                const isMenteeBlock = slot.menteeBlocksSlot;
                return (
                <Tooltip
                  key={slot.time}
                  title={isMenteeBlock && slot.menteeBlockFullLabel ? slot.menteeBlockFullLabel : undefined}
                  placement="topLeft"
                >
                  <div
                    onClick={() => canPick && handleTimeSelect(slot.time)}
                    style={{
                      border: slot.isSelected 
                        ? '2px solid #10b981' 
                        : canPick 
                          ? '1px solid #10b981' 
                          : isMenteeBlock
                            ? '1px solid #d97706'
                            : '1px solid #ef4444',
                      borderRadius: '8px',
                      padding: '12px 8px',
                      textAlign: 'center',
                      cursor: canPick ? 'pointer' : 'not-allowed',
                      background: slot.isSelected 
                        ? '#d1fae5' 
                        : canPick
                          ? '#f0fdf4' 
                          : slot.isInPast
                            ? '#f3f4f6'
                            : isMenteeBlock
                              ? '#fffbeb'
                              : '#fef2f2',
                      position: 'relative'
                    }}
                  >
                    {slot.isSelected && (
                      <div style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        width: '8px',
                        height: '8px',
                        background: 'var(--success)',
                        borderRadius: '50%'
                      }} />
                    )}
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: canPick ? '#059669' : slot.isInPast ? '#6b7280' : isMenteeBlock ? '#b45309' : '#dc2626',
                      marginBottom: '4px'
                    }}>
                      {slot.time}
                    </div>
                    <div style={{
                      fontSize: '11px',
                      lineHeight: 1.25,
                      color: canPick ? '#059669' : slot.isInPast ? '#6b7280' : isMenteeBlock ? '#b45309' : '#dc2626'
                    }}>
                      {slot.isSelected
                        ? 'Seçildi'
                        : slot.isInPast
                          ? 'Geçti'
                          : isMenteeBlock
                            ? slot.menteeBlockLabel
                            : slot.isAvailable
                              ? 'Müsait'
                              : slot.status === 'unavailable'
                                ? 'Müsait Değil'
                                : 'Uygun Değil'}
                    </div>
                  </div>
                </Tooltip>
                );
              })}
            </div>
          </div>
        </div>

        {/* Haftalık Görünüm */}
        <div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(7, 1fr)', 
            gap: '1px',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            {weekDays.map((day, index) => {
              const isPastDay = day.date.isBefore(dayjs(), 'day');
              return (
                <div
                  key={index}
                  onClick={() => !isPastDay && handleDateChange(day.date)}
                  style={{
                    background: day.date.format('YYYY-MM-DD') === selectedDate.format('YYYY-MM-DD') 
                      ? '#f3f4f6' 
                      : isPastDay 
                        ? '#f9fafb'
                        : 'white',
                    padding: '12px 8px',
                    textAlign: 'center',
                    cursor: isPastDay ? 'not-allowed' : 'pointer',
                    borderRight: index < 6 ? '1px solid #e5e7eb' : 'none',
                    opacity: isPastDay ? 0.5 : 1
                  }}
                >
                <div style={{ 
                  fontSize: '12px', 
                  color: 'var(--text-secondary)', 
                  marginBottom: '4px' 
                }}>
                  {day.dayNameShort}
                </div>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: 'bold', 
                  color: '#1f2937',
                  marginBottom: '4px'
                }}>
                  {day.dayNumber}
                </div>
                <div style={{ 
                  fontSize: '10px', 
                  color: isPastDay 
                    ? '#9ca3af'
                    : day.availableSlotCount > 0 ? '#059669' : '#dc2626'
                }}>
                  {isPastDay 
                    ? 'Geçmiş' 
                    : day.availableSlotCount > 0 ? `${day.availableSlotCount} müsait` : 'Uygun değil'
                  }
                </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Seçim Özeti */}
        {selectedTime && (
          <div style={{ 
            marginTop: '16px', 
            padding: '12px', 
            background: '#f0fdf4', 
            border: '1px solid #10b981', 
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <UserOutlined style={{ color: '#059669' }} />
            <Text style={{ color: '#059669' }}>
              {mentor.firstName} {mentor.lastName} - Müsaitlik Özeti: {selectedDate.format('DD/MM/YYYY')} {selectedTime}
            </Text>
          </div>
        )}
        </Card>
      </div>
    );
  };

  const renderBookingModal = () => {
    return (
      <Modal
        title="Randevu Talebi"
        open={bookingModalVisible}
        onCancel={handleBookingCancel}
        footer={[
          <Button key="cancel" onClick={handleBookingCancel}>
            İptal
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            loading={bookingMutation.isPending}
            onClick={() => form.submit()}
          >
            Randevu Talebi Gönder
          </Button>
        ]}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleBookingSubmit}
        >
          <div style={{ marginBottom: '16px', padding: '12px', background: '#f0fdf4', border: '1px solid #10b981', borderRadius: '8px' }}>
            <Text strong style={{ color: '#059669' }}>
              Randevu Detayları
            </Text>
            <br />
            <Text style={{ color: '#059669' }}>
              Mentor: {mentor?.firstName} {mentor?.lastName}
            </Text>
            <br />
            <Text style={{ color: '#059669' }}>
              Tarih: {selectedDate.format('DD MMMM YYYY dddd')}
            </Text>
            <br />
            <Text style={{ color: '#059669' }}>
              Saat: {selectedTime} - {selectedTime ? selectedDate.hour(parseInt(selectedTime.split(':')[0])).minute(0).second(0).add(1, 'hour').format('HH:mm') : ''}
            </Text>
            <br />
            <Text style={{ color: '#059669' }}>
              Ücret: ₺{mentor?.hourlyRate || 0}/saat
            </Text>
          </div>

          <Form.Item
            name="notes"
            label="Notlar (İsteğe bağlı)"
            rules={[
              { max: 500, message: 'Notlar en fazla 500 karakter olabilir' }
            ]}
          >
            <TextArea 
              rows={4} 
              placeholder="Mentor ile paylaşmak istediğiniz notlar..."
            />
          </Form.Item>
        </Form>
      </Modal>
    );
  };

  if (!mentor && !loading) {
  return (
    <div className="app-page">
      <div className="app-empty-card">
        <Title level={3} type="secondary">Mentor bulunamadı</Title>
        <Button onClick={() => navigate('/mentor-search')}>
          <ArrowLeftOutlined /> Geri Dön
        </Button>
      </div>
    </div>
    );
  }

  return (
    <div className="app-page">
      <div className="app-container">
        <div style={{ marginBottom: '24px' }}>
          <Button 
            onClick={() => navigate('/mentor-search')} 
            icon={<ArrowLeftOutlined />}
            aria-label="Mentor listesi sayfasına geri dön"
          >
            Geri Dön
          </Button>
        </div>

        {renderHeader()}
        {renderSkills()}
        {renderEducations()}
        {renderWorkExperiences()}
        {renderProjects()}
        {renderCertificates()}
        {renderBootcamps()}
        {renderPublishedBootcamps()}
        {renderServices()}
        {renderAvailability()}
        {renderBookingModal()}
      </div>
    </div>
  );
};

export default MentorDetail;
